import { useRef, useEffect } from "react";

/**
 * Hook that sets up an interval and clears it on unmount.
 * The callback will be called repeatedly at the specified delay interval.
 *
 * @param callback - Function to call at each interval
 * @param delay - Delay in milliseconds between calls, or null to pause the interval
 *
 * @example
 * useInterval(() => {
 *   console.log('Called every second');
 * }, 1000);
 *
 * // Pause the interval by passing null
 * useInterval(callback, isPaused ? null : 1000);
 */
const useInterval = (callback: () => void, delay: number | null): void => {
  const savedCallback = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay !== null) {
      const id = setInterval(() => {
        if (savedCallback.current) {
          savedCallback.current();
        }
      }, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

export default useInterval;
